import { Difficulty, EntryType } from "../../API";
import { ExplorationPotential, TriState, Visibility, WaterType, Rating } from "../../models";
import { ReportVisibility, ReportWind, ReportSwell } from "../../models";
import {
    CaveRating,
    LocationType,
    TourismPotential,
    DisabledAccessPotential,
    MediaContentType,
} from "../../models";
import { Kernel } from "../Kernel";
import { ILocation } from "./ILocation";
import omit from "lodash-es/omit.js";
import cloneDeep from "lodash-es/cloneDeep.js";

export const AWS_META_PROPS = ["updatedAt", "createdAt", "_deleted", "_version", "_lastChangedAt"];

export function awsToSimpleObject(item: any, ignoreProps: string[] = []): any {
    return omit(cloneDeep(item), [...AWS_META_PROPS, ...ignoreProps]);
}
export interface IReference {
    name: string;
    text: string;
}

export interface IS3Object {
    bucket: string;
    region: string;
    key: string;
}

export interface IS3ObjectInput {
    bucket: string;
    region: string;
    localUri: string;
    visibility: Visibility;
    key: string;
    mimeType: string;
}

export interface IMedia {
    id: string;
    contributorId?: string | null;
    mimeType: string;
    file: IS3Object;
    thumbnail_512px?: IS3Object | null;
    thumbnail_1024px?: IS3Object | null;
    name?: string | null;
    description?: string | null;
    attribution?: string | null;
    attributionLink?: string | null;
    contentType?: MediaContentType | keyof typeof MediaContentType | null;
    dateAdded?: string | null;
    dateCreated?: string | null;
}

export interface IPlaceMediaItem {
    id: string;
    owner: string;
    groupAccess?: string[] | null;
    media: IMedia;
    placeMediaId: string;
    createdAt: string;
}

export interface ISpelioData {
    type: LocationType | keyof typeof LocationType;
    source: string;
    reference: string;
    notes: string;
    tourismPotential: TourismPotential | keyof typeof TourismPotential;
    accessDetails: string;
    verticalAccess: TriState | keyof typeof TriState;
    caveRating: CaveRating | keyof typeof CaveRating;
    highestCaveRating?: CaveRating | keyof typeof CaveRating | null;
    explorationPotential: ExplorationPotential | keyof typeof ExplorationPotential;
    disabledAccessPotential: DisabledAccessPotential | keyof typeof DisabledAccessPotential;
    files: string[];
    labels: string[];
    goesToWater: TriState | keyof typeof TriState;
    references: Array<IReference>;
    systemName?: string | null;
    surveyedLength?: number | null;
}

export interface IDiveData {
    minDepth?: number | null;
    maxDepth?: number | null;
    minTemp?: number | null;
    maxTemp?: number | null;
    waterType: WaterType | keyof typeof WaterType;
    isTidal: TriState | keyof typeof TriState;
    entryType?: EntryType | null;
    difficulty?: Difficulty | null;
    entryDifficulty?: Difficulty | null;
}

export interface IAddress {
    streetNumber?: string | null;
    street?: string | null;
    suburb?: string | null;
    state?: string | null;
    country?: string | null;
    postcode?: string | null;
    timezone?: string | null;
}

export interface IPlaceReport {
    id: string;
    rating: number;
    text: string;
    date: string;
    owner: string;
    visibility?: ReportVisibility | null;
    wind?: ReportWind | null;
    swell?: ReportSwell | null;
    waterTemp?: number | null;
    placeReportsId?: string | null;
}

export interface IPlaceRating {
    id: string;
    rating: number;
    place: string;
}

export interface IAmenities {
    hasShowers: TriState;
    hasToilets: TriState;
    hasPhone?: TriState | null;
    food?: Rating | null;
    family?: Rating | null;
    parking?: Rating | null;
}

export interface IWreckData {
    type?: string | null;
    built?: string | null;
    dateSunk?: string | null;
    length?: number | null;
    width?: number | null;
    draught?: number | null;
}

export interface IPlaceData {
    name: string;
    alternateNames: string[];
    location: ILocation;
    locationKnown: boolean;
    checked: boolean;
    checkedBy: string;
    spelioData: ISpelioData;
    diveData?: IDiveData | null;
    wreckData?: IWreckData | null;
    media?: IMedia[] | null;
    altitude?: number | null;
    address?: IAddress | null;
    warnings?: string[] | null;
    amenities?: IAmenities | null;
}

export interface IPlace {
    id: string;
    softDeleted?: string | null;
    data: IPlaceData;
    userAccess?: string[] | null;
    groupAccess?: string[] | null;
    scubaCollection?: string | null;
    caveCollection?: string | null;
}

export function makeDefaultDiveData() {
    return {
        maxDepth: undefined,
        minTemp: undefined,
        maxTemp: undefined,
        waterType: WaterType.UNKNOWN,
        isTidal: TriState.UNKNOWN,
        warnings: [],
    } as IDiveData;
}

export function makeEmptySpelioData() {
    return {
        type: LocationType.UNKNOWN,
        source: "",
        reference: "",
        notes: "",
        caveRating: CaveRating.UNKNOWN,
        highestCaveRating: CaveRating.UNKNOWN,
        tourismPotential: TourismPotential.UNKNOWN,
        verticalAccess: TriState.UNKNOWN,
        accessDetails: "",
        explorationPotential: ExplorationPotential.UNKNOWN,
        disabledAccessPotential: DisabledAccessPotential.UNKNOWN,
        files: [],
        labels: [],
        references: [],
        goesToWater: TriState.UNKNOWN,
    } as ISpelioData;
}

export function makeEmptyAmenities() {
    return {
        hasShowers: TriState.UNKNOWN,
        hasToilets: TriState.UNKNOWN,
        hasPhone: TriState.UNKNOWN,
        food: Rating.UNKNOWN,
        family: Rating.UNKNOWN,
        parking: Rating.UNKNOWN,
    } as IAmenities;
}

export function makeEmptyPlaceData() {
    return {
        name: "Unnamed",
        alternateNames: [],
        location: { lat: 0, lon: 0 },
        checked: false,
        checkedBy: "",
        locationKnown: true,
        spelioData: makeEmptySpelioData(),
        diveData: undefined,
        media: [],
        amenities: makeEmptyAmenities(),
    } as IPlaceData;
}

export function makeEmptyPlace() {
    return {
        id: "",
        deleted: false,
        history: [],
        data: makeEmptyPlaceData(),
        groupAccess: ["public", "publicReadonly"],
        userAccess: [],
        scubaCollection: Kernel.instance().collection === "s" ? "true" : "false",
        caveCollection: Kernel.instance().collection === "c" ? "true" : "false",
    } as IPlace;
}

export function makeEmptyPlaceReport() {
    return {
        id: "",
        rating: 0,
        text: "",
        visibility: ReportVisibility.UNSPECIFIED,
        swell: ReportSwell.UNSPECIFIED,
        wind: ReportWind.UNSPECIFIED,
        waterTemp: undefined,
        owner: "",
        date: new Date().toISOString(),
    } as IPlaceReport;
}

export function makeDefaultWreckData() {
    return {
        name: null,
        built: null,
        dateSunk: null,
        length: null,
        width: null,
        draught: null,
    } as IWreckData;
}
